import { useTranslation } from "react-i18next"

// Segmented pill for a binary language toggle.
//
// Why a pill and not a dropdown:
//   • Affordance — the inactive language is visible, so users know it
//     exists without having to open anything first.
//   • Single click to swap (no open → pick).
//   • State is visually obvious: the active half is filled; the other
//     half is flat.
//
// Each label is rendered in its own script (`lang` attribute set per
// option) so a user scanning the header doesn't have to translate a
// language name back into their own alphabet to identify it. The
// `name` is used only for assistive tech via aria-label.
type LangCode = "en" | "ar"

interface LangDef {
  code: LangCode
  short: string
  name: string
}

const LANGUAGES: LangDef[] = [
  { code: "en", short: "EN", name: "English" },
  { code: "ar", short: "ع", name: "العربية" },
]

interface LanguageSwitcherProps {
  // Fired whenever the user picks a new language. The caller is
  // responsible for propagating the change to app-level state (Redux,
  // user preferences) — this component stays purely presentational.
  onChange?: (code: LangCode) => void
}

const LanguageSwitcher = ({ onChange }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation()

  // i18n.language can be "en-US", "ar-SA", etc. Normalize to our two
  // supported codes so the active state is always determinate.
  const current: LangCode = i18n.language?.toLowerCase().startsWith("ar")
    ? "ar"
    : "en"

  const switchTo = (code: LangCode) => {
    if (code === current) return
    i18n.changeLanguage(code)
    // Mirror language + direction onto <html> so CSS, screen readers,
    // and third-party widgets all see a consistent locale.
    const html = document.documentElement
    html.setAttribute("dir", code === "ar" ? "rtl" : "ltr")
    html.setAttribute("lang", code)
    onChange?.(code)
  }

  return (
    <div
      className="lang-switch"
      role="radiogroup"
      aria-label="Language"
    >
      {LANGUAGES.map((l) => {
        const active = l.code === current
        return (
          <button
            key={l.code}
            type="button"
            role="radio"
            aria-checked={active}
            className={`lang-switch__opt ${active ? "is-active" : ""}`}
            onClick={() => switchTo(l.code)}
            lang={l.code}
            aria-label={l.name}
          >
            {l.short}
          </button>
        )
      })}
    </div>
  )
}

export default LanguageSwitcher
